import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Camelot Wheel colors - vibrant gradient-ready colors
const CAMELOT_COLORS = {
  '1A': '#FF5E5E', '1B': '#FF8585',   // Red
  '2A': '#FF8C42', '2B': '#FFAB6B',   // Orange
  '3A': '#FFD166', '3B': '#FFE08F',   // Yellow
  '4A': '#C5E063', '4B': '#D4EB8C',   // Yellow-Green
  '5A': '#7ED957', '5B': '#A5E88C',   // Green
  '6A': '#57D9A3', '6B': '#8CE8C5',   // Teal
  '7A': '#57D9D9', '7B': '#8CE8E8',   // Cyan
  '8A': '#5B9FE3', '8B': '#8CC1F0',   // Light Blue
  '9A': '#5E6BE3', '9B': '#8C95F0',   // Blue
  '10A': '#9B59E3', '10B': '#B98CF0', // Purple
  '11A': '#D957D9', '11B': '#E88CE8', // Magenta
  '12A': '#E3598C', '12B': '#F08CB3'  // Pink
};

// Match type colors
const MATCH_COLORS = {
  same: '#00FF88',      // Same key - green
  boost: '#FFD166',     // +1 energy - yellow
  drop: '#5B9FE3',      // -1 energy - blue
  relative: '#D957D9'   // Relative major/minor - magenta
};

const CamelotWheel = ({ 
  selectedKey, 
  onSelectKey, 
  showCompatible = true,
  size = 'lg',
  onClose
}) => {
  const [compatibleKeys, setCompatibleKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoveredKey, setHoveredKey] = useState(null);

  useEffect(() => {
    if (selectedKey && showCompatible) {
      fetchCompatibleKeys(selectedKey);
    } else {
      setCompatibleKeys([]);
    }
  }, [selectedKey, showCompatible]);

  const fetchCompatibleKeys = async (key) => {
    try {
      setLoading(true);
      const response = await api.get(`/camelot-compatible/${key}`, {
        withCredentials: true
      });
      setCompatibleKeys(response.data.compatible_keys || []);
    } catch (error) {
      console.error('Error fetching compatible keys:', error);
      setCompatibleKeys([]);
    } finally {
      setLoading(false);
    }
  };

  const isCompatible = (key) => {
    return compatibleKeys.some(c => c.key === key);
  };

  const getMatchInfo = (key) => {
    return compatibleKeys.find(c => c.key === key);
  };

  // Size configurations
  const sizeConfig = {
    sm: { width: 280, outerR: 110, innerR: 70, centerR: 40, keySize: 24 },
    md: { width: 360, outerR: 140, innerR: 90, centerR: 50, keySize: 30 },
    lg: { width: 420, outerR: 165, innerR: 105, centerR: 55, keySize: 36 }
  };

  const config = sizeConfig[size];
  const center = config.width / 2;

  // Generate key positions
  const getKeyPosition = (index, isOuter) => {
    const angle = ((index - 3) * 30) * (Math.PI / 180); // Start from top (12 o'clock)
    const radius = isOuter ? config.outerR : config.innerR;
    return {
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle)
    };
  };

  // Handle key click
  const handleKeyClick = (key) => {
    if (onSelectKey) {
      if (selectedKey === key) {
        onSelectKey(null); // Deselect if clicking same key
      } else {
        onSelectKey(key);
      }
    }
  };

  // Render a single key button
  const renderKey = (keyNum, type) => {
    const key = `${keyNum}${type}`;
    const isOuter = type === 'B';
    const pos = getKeyPosition(keyNum, isOuter);
    const isSelected = selectedKey === key;
    const isHovered = hoveredKey === key;
    const matchInfo = getMatchInfo(key);
    const compatible = isCompatible(key);
    
    let borderColor = 'rgba(255,255,255,0.1)';
    let borderWidth = 2;
    let glowColor = 'transparent';
    
    if (isSelected) {
      borderColor = '#fff';
      borderWidth = 3;
      glowColor = CAMELOT_COLORS[key];
    } else if (compatible && selectedKey) {
      borderColor = matchInfo?.match === 'same' ? MATCH_COLORS.same :
                    matchInfo?.match === 'boost' ? MATCH_COLORS.boost :
                    matchInfo?.match === 'drop' ? MATCH_COLORS.drop :
                    MATCH_COLORS.relative;
      borderWidth = 3;
      glowColor = borderColor;
    }

    return (
      <motion.g
        key={key}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: (keyNum - 1) * 0.03 + (isOuter ? 0.015 : 0), type: 'spring' }}
        style={{ cursor: 'pointer' }}
        onClick={() => handleKeyClick(key)}
        onMouseEnter={() => setHoveredKey(key)}
        onMouseLeave={() => setHoveredKey(null)}
      >
        {/* Glow effect */}
        {(isSelected || (compatible && selectedKey)) && (
          <circle
            cx={pos.x}
            cy={pos.y}
            r={config.keySize / 2 + 6}
            fill="none"
            stroke={glowColor}
            strokeWidth="2"
            opacity="0.5"
            style={{ filter: 'blur(4px)' }}
          />
        )}
        
        {/* Key circle */}
        <circle
          cx={pos.x}
          cy={pos.y}
          r={config.keySize / 2}
          fill={CAMELOT_COLORS[key]}
          stroke={borderColor}
          strokeWidth={borderWidth}
          style={{
            filter: isHovered || isSelected ? 'brightness(1.2)' : 'brightness(1)',
            transition: 'filter 0.2s ease'
          }}
        />
        
        {/* Key label */}
        <text
          x={pos.x}
          y={pos.y}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#000"
          fontSize={config.keySize / 2.8}
          fontWeight="bold"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {key}
        </text>
        
        {/* Track count badge for compatible keys */}
        {compatible && selectedKey && matchInfo?.track_count > 0 && (
          <g>
            <rect
              x={pos.x + config.keySize / 3}
              y={pos.y - config.keySize / 2 - 4}
              width={20}
              height={12}
              rx={6}
              fill="#000"
              opacity="0.8"
            />
            <text
              x={pos.x + config.keySize / 3 + 10}
              y={pos.y - config.keySize / 2 + 2}
              textAnchor="middle"
              dominantBaseline="middle"
              fill="#fff"
              fontSize="8"
              fontWeight="bold"
            >
              {matchInfo.track_count}
            </text>
          </g>
        )}
      </motion.g>
    );
  };

  return (
    <div className="flex flex-col items-center gap-6 p-4">
      {/* Header */}
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          <span className="text-xl">🎡</span>
          <h3 className="text-white font-bold text-lg">Camelot Wheel</h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ✕
          </button>
        )}
      </div>
      
      <p className="text-gray-400 text-sm text-center">
        {selectedKey 
          ? `Mostrando keys compatibles con ${selectedKey}`
          : 'Selecciona un key para ver compatibilidad'
        }
      </p>

      {/* Wheel Container */}
      <div className="relative">
        <svg 
          width={config.width} 
          height={config.width} 
          viewBox={`0 0 ${config.width} ${config.width}`}
          className="drop-shadow-2xl"
        >
          {/* Outer ring background */}
          <circle
            cx={center}
            cy={center}
            r={config.outerR + config.keySize / 2 + 10}
            fill="#0D0D0D"
            stroke="#1A1A1A"
            strokeWidth="1"
          />
          
          {/* Middle ring */}
          <circle
            cx={center}
            cy={center}
            r={(config.outerR + config.innerR) / 2}
            fill="none"
            stroke="#1A1A1A"
            strokeWidth="1"
            strokeDasharray="4 4"
            opacity="0.5"
          />
          
          {/* Inner ring background */}
          <circle
            cx={center}
            cy={center}
            r={config.innerR - config.keySize / 2 - 5}
            fill="#141414"
            stroke="#1A1A1A"
            strokeWidth="1"
          />
          
          {/* Center circle */}
          <circle
            cx={center}
            cy={center}
            r={config.centerR}
            fill="#0A0A0A"
            stroke="#262626"
            strokeWidth="2"
          />
          
          {/* Center text */}
          <text
            x={center}
            y={center - 8}
            textAnchor="middle"
            fill="#666"
            fontSize="12"
            fontWeight="bold"
          >
            CAMELOT
          </text>
          <text
            x={center}
            y={center + 8}
            textAnchor="middle"
            fill="#444"
            fontSize="9"
          >
            WHEEL
          </text>
          
          {/* Ring labels */}
          <text
            x={center}
            y={center - config.innerR - config.keySize / 2 - 12}
            textAnchor="middle"
            fill="#555"
            fontSize="10"
          >
            Minor (A)
          </text>
          <text
            x={center}
            y={center - config.outerR - config.keySize / 2 - 12}
            textAnchor="middle"
            fill="#555"
            fontSize="10"
          >
            Major (B)
          </text>
          
          {/* Keys - Inner ring (A keys) */}
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => renderKey(num, 'A'))}
          
          {/* Keys - Outer ring (B keys) */}
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => renderKey(num, 'B'))}
        </svg>
        
        {/* Loading overlay */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
            <div className="animate-spin w-8 h-8 border-2 border-[#00D9FF] border-t-transparent rounded-full" />
          </div>
        )}
      </div>

      {/* Legend */}
      <AnimatePresence>
        {selectedKey && compatibleKeys.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-[#141414] rounded-xl p-4 border border-[#262626] w-full max-w-sm"
          >
            <h4 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
              <span className="text-lg">🎯</span>
              Keys compatibles con {selectedKey}
            </h4>
            
            <div className="space-y-2">
              {compatibleKeys.map((item) => (
                <motion.button
                  key={item.key}
                  onClick={() => handleKeyClick(item.key)}
                  whileHover={{ scale: 1.02, x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center justify-between w-full p-2 rounded-lg bg-[#0D0D0D] hover:bg-[#1A1A1A] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-black"
                      style={{ backgroundColor: CAMELOT_COLORS[item.key] }}
                    >
                      {item.key}
                    </div>
                    <div className="text-left">
                      <span className="text-white font-medium text-sm">{item.key}</span>
                      <p className="text-gray-500 text-xs">{item.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span 
                      className="w-2 h-2 rounded-full"
                      style={{ 
                        backgroundColor: item.match === 'same' ? MATCH_COLORS.same :
                                        item.match === 'boost' ? MATCH_COLORS.boost :
                                        item.match === 'drop' ? MATCH_COLORS.drop :
                                        MATCH_COLORS.relative
                      }}
                    />
                    <span className="text-xs font-bold px-2 py-1 rounded-full bg-[#262626] text-gray-300">
                      {item.track_count} tracks
                    </span>
                  </div>
                </motion.button>
              ))}
            </div>
            
            {/* Legend explanation */}
            <div className="mt-4 pt-3 border-t border-[#262626] grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: MATCH_COLORS.same }} />
                <span className="text-gray-400">Misma tonalidad</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: MATCH_COLORS.boost }} />
                <span className="text-gray-400">+1 energía</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: MATCH_COLORS.drop }} />
                <span className="text-gray-400">-1 energía</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: MATCH_COLORS.relative }} />
                <span className="text-gray-400">Relativo mayor/menor</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Instructions */}
      {!selectedKey && (
        <div className="text-center text-gray-500 text-xs max-w-xs">
          <p>💡 Tip: Usa el sistema Camelot para mezclar tracks armónicamente.</p>
          <p className="mt-1">Keys adyacentes (+1/-1) crean transiciones suaves.</p>
        </div>
      )}
    </div>
  );
};

// Simple Key Badge component for track cards
export const KeyBadge = ({ keyValue, onClick, size = 'sm' }) => {
  if (!keyValue) return null;
  
  const color = CAMELOT_COLORS[keyValue] || '#666';
  
  return (
    <button
      onClick={onClick}
      className={`
        inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-bold
        transition-all duration-200 hover:scale-110 hover:shadow-lg
        ${size === 'sm' ? 'text-xs' : 'text-sm'}
      `}
      style={{ 
        backgroundColor: `${color}30`,
        color: color,
        border: `1px solid ${color}50`
      }}
      title={`Key: ${keyValue} - Click para filtrar`}
    >
      {keyValue}
    </button>
  );
};

export default CamelotWheel;
